// Minimal Matomo SPA tracker helper (anonymized, low-noise by default)
// It assumes Matomo is available through the reverse proxy at /matomo/

let matomoInitialized = false;

const recentlySent = new Map(); // key -> ts
let lastPageView = { url: null, ts: 0 };

function nowMs() {
  return Date.now();
}

function normalizeBasePath(basePath) {
  const p = String(basePath || "/matomo/").trim() || "/matomo/";
  return p.endsWith("/") ? p : `${p}/`;
}

function shouldSample(sampleRate) {
  if (sampleRate === undefined || sampleRate === null) return true;
  const r = Number(sampleRate);
  if (!Number.isFinite(r)) return true;
  if (r <= 0) return false;
  if (r >= 1) return true;
  return Math.random() < r;
}

function shouldDedupe(key, dedupeMs) {
  const windowMs = Number(dedupeMs);
  if (!Number.isFinite(windowMs) || windowMs <= 0) return false;
  const t = nowMs();
  const prev = recentlySent.get(key);
  recentlySent.set(key, t);
  if (prev === undefined) return false;
  return (t - prev) < windowMs;
}

function getPaq() {
  if (typeof window === "undefined") return null;
  return (window._paq = window._paq || []);
}

export function initMatomo(siteId, basePath = "/matomo/") {
  if (matomoInitialized) return;
  if (!siteId || typeof window === "undefined") return;

  const _paq = getPaq();
  if (!_paq) return;

  const bp = normalizeBasePath(basePath);

  // Privacy-first defaults (keeps your current "anonymous" intent)
  _paq.push(["setDoNotTrack", true]);
  _paq.push(["disableCookies"]);
  _paq.push(["enableHeartBeatTimer", 15]);

  _paq.push(["setTrackerUrl", `${bp}matomo.php`]);
  _paq.push(["setSiteId", String(siteId)]);
  _paq.push(["enableLinkTracking"]);

  // Avoid injecting multiple scripts (can cause duplicate hits)
  const existing = document.querySelector(`script[src="${bp}matomo.js"]`);
  if (!existing) {
    const g = document.createElement("script");
    const s = document.getElementsByTagName("script")[0];
    g.async = true;
    g.src = `${bp}matomo.js`;
    s?.parentNode?.insertBefore(g, s);
  }

  matomoInitialized = true;
}

export function trackSpaNavigation(url, opts = {}) {
  const _paq = getPaq();
  if (!_paq) return;

  const dedupeMs = opts?.dedupeMs ?? 800;
  const fullUrl = String(url || "");
  if (!fullUrl) return;

  const t = nowMs();
  if (lastPageView.url === fullUrl && (t - lastPageView.ts) < dedupeMs) return;
  lastPageView = { url: fullUrl, ts: t };

  // For SPAs, Matomo recommends setCustomUrl + setDocumentTitle + trackPageView
  _paq.push(["setCustomUrl", fullUrl]);
  _paq.push(["setDocumentTitle", document.title]);
  _paq.push(["trackPageView"]);
}

// Backward compatible:
// - trackEvent(category, action)
// - trackEvent(category, action, name)
// - trackEvent(category, action, name, value)
// - trackEvent(category, action, name, value, {dedupeMs, sampleRate})
// - trackEvent(category, action, name, {dedupeMs, sampleRate})
export function trackEvent(category, action, name, value, options) {
  const _paq = getPaq();
  if (!_paq) return;

  const categoryStr = String(category || "");
  const actionStr = String(action || "");
  if (!categoryStr || !actionStr) return;

  let eventName = name;
  let eventValue = value;
  let opts = options;

  // Support trackEvent(cat, act, name, { ...opts })
  if (eventValue && typeof eventValue === "object" && !Array.isArray(eventValue)) {
    opts = eventValue;
    eventValue = undefined;
  }

  const dedupeMs = opts?.dedupeMs ?? 800;
  const sampleRate = opts?.sampleRate ?? 1;
  if (!shouldSample(sampleRate)) return;

  const nameStr = eventName == null ? "" : String(eventName);
  const dedupeKey = `event|${categoryStr}|${actionStr}|${nameStr}`;
  if (shouldDedupe(dedupeKey, dedupeMs)) return;

  if (typeof eventValue === "number") {
    _paq.push(["trackEvent", categoryStr, actionStr, nameStr, eventValue]);
  } else {
    _paq.push(["trackEvent", categoryStr, actionStr, nameStr]);
  }
}

export function trackGoal(goalId, revenue, opts = {}) {
  const _paq = getPaq();
  if (!_paq) return;

  const id = Number(goalId);
  if (!Number.isFinite(id)) return;

  const dedupeMs = opts?.dedupeMs ?? 2000;
  const sampleRate = opts?.sampleRate ?? 1;
  if (!shouldSample(sampleRate)) return;

  const dedupeKey = `goal|${id}`;
  if (shouldDedupe(dedupeKey, dedupeMs)) return;

  if (typeof revenue === "number" && Number.isFinite(revenue)) {
    _paq.push(["trackGoal", id, revenue]);
  } else {
    _paq.push(["trackGoal", id]);
  }
}

export function trackOncePerSession(key, fn) {
  if (typeof window === "undefined") return;
  const k = `matomo_once_${String(key || "")}`;
  if (!k) return;
  try {
    if (window.sessionStorage?.getItem(k) === "1") return;
    window.sessionStorage?.setItem(k, "1");
  } catch (_) {
    // ignore storage errors (private mode)
  }
  try {
    fn?.();
  } catch (_) {
    // keep tracking failures non-blocking
  }
}



